import {Component, Input, OnInit} from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {NgIf} from "@angular/common";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ToastrService} from "ngx-toastr";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {UserLicensesService} from "@shared/api/services/v2/user-licenses.service";
import {DEACTIVATION_REASON_MAX_LENGTH} from "@shared/api/models/command/user-license.model";

/**
 * UNIVAPLAT-2772: admin-dashboard modal to deactivate a user's active license
 * (optionally recording a reason) or to edit the deactivation reason of the
 * most recent deactivated license period.
 *
 * Two modes via `@Input() mode`:
 * - `deactivate` — reason is OPTIONAL (backend contract UNIVAPLAT-2756 accepts an
 *   absent reason); submits `POST .../license/deactivate` via
 *   {@link UserLicensesService.deactivate}.
 * - `editReason` — reason is REQUIRED (whitespace-only rejected — see
 *   {@link trimmedRequired}); submits
 *   `PATCH .../license/{licenseId}/deactivation-reason` via
 *   {@link UserLicensesService.updateDeactivationReason}. Two ways to target
 *   the license period:
 *   - explicit `@Input() licenseId` (+ `initialReason` pre-fill) — used by the
 *     license history modal (UNIVAPLAT-2774) so ANY past period's reason can be
 *     edited from the history list; no extra history request is made.
 *   - no `licenseId` — dashboard-row shortcut: the row only carries the user's
 *     ACTIVE license (null once deactivated), so this mode loads the license
 *     history on init, targets the newest deactivated period (first entry with a
 *     non-null `expiredAt` — history is newest-first) and pre-fills its current
 *     reason.
 *
 * `Validators.maxLength(DEACTIVATION_REASON_MAX_LENGTH)` mirrors the backend's
 * 422 `deactivation_reason_too_long` limit client-side in both modes.
 *
 * Backend owns ALL user-facing error text (409 no_active_license, 422
 * over-length, 404): errors surface verbatim via `toastr.error(error.error.error)`
 * and the modal stays open. On success: toastr.success + `modal.close(true)` so
 * the dashboard page refreshes its rows.
 *
 * The reason is PHI-adjacent — never log it (Sentry breadcrumbs included).
 */

/**
 * `Validators.required` accepts whitespace-only strings, but `submit()` trims the
 * value — so "   " would slip past the client-side gate and PATCH an empty reason.
 * Reject any value that trims to '' with the same `required` error key so the
 * existing template error message applies unchanged.
 */
const trimmedRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim() === '' ? {required: true} : null;

@Component({
    selector: 'cdss-license-deactivate-modal',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        NgIf,
        SpinnerComponent
    ],
    templateUrl: './license-deactivate.modal.html'
})
export class LicenseDeactivateModal implements OnInit {

    @Input() mode: 'deactivate' | 'editReason' = 'deactivate';
    @Input() userId: number;
    /** Display-only context in the modal header; may arrive masked (PHI masking). */
    @Input() userName = '';
    /**
     * editReason mode: explicit id of the user-license period to edit
     * (UNIVAPLAT-2757 review pass 1 — set by the license history modal so ANY
     * past period can be targeted). When null, the newest deactivated period is
     * resolved from the history on init (dashboard-row shortcut).
     */
    @Input() licenseId: number | null = null;
    /**
     * editReason mode with an explicit `licenseId`: the period's current reason,
     * already loaded by the caller (history modal) — pre-fills the form without
     * an extra history request.
     */
    @Input() initialReason: string | null = null;

    readonly maxLength = DEACTIVATION_REASON_MAX_LENGTH;

    loading = false;
    saving = false;
    /**
     * editReason mode only: the user has no deactivated license period in their
     * history, so there is nothing to PATCH — submit is disabled.
     */
    historyMissing = false;

    form = new FormGroup({
        reason: new FormControl<string>('', [Validators.maxLength(DEACTIVATION_REASON_MAX_LENGTH)]),
    });

    constructor(
        public modal: NgbActiveModal,
        private userLicensesService: UserLicensesService,
        private toastr: ToastrService,
    ) {
    }

    async ngOnInit() {
        if (this.mode !== 'editReason') {
            return;
        }
        // Reason is only mandatory when editing an existing deactivation reason.
        // trimmedRequired (not Validators.required) so whitespace-only input —
        // which submit() would trim to '' — is rejected client-side too.
        this.form.controls.reason.addValidators(trimmedRequired);
        this.form.controls.reason.updateValueAndValidity();

        if (this.licenseId !== null) {
            // Explicit period targeted (opened from the license history modal):
            // no newest-period resolution needed — pre-fill from the entry the
            // caller already loaded.
            this.form.controls.reason.setValue(this.initialReason ?? '');
            return;
        }

        this.loading = true;
        try {
            const history = await this.userLicensesService.listHistory(this.userId);
            // Newest-first: the first expired entry is the most recent deactivation.
            const deactivated = history.find(entry => entry.expiredAt !== null);
            if (deactivated) {
                this.licenseId = deactivated.id;
                this.form.controls.reason.setValue(deactivated.deactivationReason ?? '');
            } else {
                this.historyMissing = true;
            }
        } catch (error: any) {
            // Backend owns the message (e.g. 404 unknown user) — surface verbatim.
            this.toastr.error(error?.error?.error);
            this.modal.close(false);
        } finally {
            this.loading = false;
        }
    }

    async submit() {
        if (this.saving || this.loading || this.historyMissing) {
            return;
        }
        if (this.form.invalid) {
            // Client-side gate: required (editReason) and maxLength(100) — the form
            // is invalid and submission is prevented (acceptance criteria).
            this.form.markAllAsTouched();
            return;
        }

        const reason = (this.form.value.reason ?? '').trim();
        this.saving = true;
        try {
            if (this.mode === 'deactivate') {
                // Empty reason → deactivate WITHOUT a recorded reason (omit the field).
                await this.userLicensesService.deactivate(this.userId, reason !== '' ? reason : undefined);
                this.toastr.success('License deactivated', 'Success');
            } else {
                await this.userLicensesService.updateDeactivationReason(this.userId, this.licenseId!, reason);
                this.toastr.success('Deactivation reason updated', 'Success');
            }
            this.modal.close(true);
        } catch (error: any) {
            // Backend owns the user-facing error text (409 no_active_license,
            // 422 deactivation_reason_too_long, 404) — no hardcoded frontend copy.
            // The modal stays open so the admin can correct and retry.
            this.toastr.error(error?.error?.error);
        } finally {
            this.saving = false;
        }
    }
}
